/**
 * Bulk Upload Component
 */
import React, { useState } from 'react';
import { Upload, Download, FileSpreadsheet, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BulkUpload = ({ onUpload }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFileSelect = (file) => {
    if (file && file.name.endsWith('.csv')) {
      setSelectedFile(file);
      setBulkResult(null);
    } else {
      toast.error('Please select a CSV file');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  };

  const handleBulkUpload = async () => {
    if (!selectedFile) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);
    
    try {
      const response = await axios.post(`${API}/admin/bulk-create`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setBulkResult(response.data);
      setSelectedFile(null);
      toast.success(`Created ${response.data.created_count} tags`);
      if (onUpload) onUpload();
    } catch (error) {
      toast.error('Bulk upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto">
      <div className="rounded-3xl border border-border/40 bg-card/50 p-8">
        <h2 className="text-2xl font-bold mb-6">Bulk Upload</h2>
        
        {/* Drop Zone */}
        <div 
          className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${
            dragOver 
              ? 'border-primary bg-primary/5' 
              : selectedFile 
                ? 'border-green-500 bg-green-500/5' 
                : 'border-border hover:border-primary/50'
          }`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
        >
          <input
            type="file"
            accept=".csv"
            onChange={(e) => handleFileSelect(e.target.files?.[0])}
            className="hidden"
            id="csv-upload"
          />
          <label htmlFor="csv-upload" className="cursor-pointer block">
            {selectedFile ? (
              <>
                <FileSpreadsheet className="w-12 h-12 mx-auto text-green-500 mb-4" />
                <p className="font-medium text-green-600">{selectedFile.name}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {(selectedFile.size / 1024).toFixed(1)} KB
                </p>
              </>
            ) : (
              <>
                <Upload className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="font-medium">Drop CSV file here or click to browse</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Format: nfc_code, description, message
                </p>
              </>
            )}
          </label>
        </div>

        {/* Actions */}
        <div className="flex gap-2 mt-4">
          <button
            onClick={handleBulkUpload}
            disabled={!selectedFile || uploading}
            className="flex-1 bg-primary text-primary-foreground rounded-full py-3 font-medium hover:scale-105 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="bulk-upload-btn"
          >
            {uploading ? (
              <>
                <span className="animate-spin">⏳</span>
                Uploading...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                Upload
              </>
            )}
          </button>
          <a
            href={`${API}/admin/csv-template`}
            className="px-6 bg-secondary text-secondary-foreground rounded-full py-3 font-medium hover:bg-secondary/80 transition-all flex items-center gap-2"
            data-testid="download-template-btn"
          >
            <Download className="w-4 h-4" />
            Template
          </a>
        </div>

        {/* Results */}
        {bulkResult && (
          <div className="mt-6 p-4 bg-secondary/30 rounded-xl">
            <div className="flex items-center gap-2 mb-3">
              {bulkResult.error_count === 0 ? (
                <CheckCircle className="w-5 h-5 text-green-500" />
              ) : (
                <AlertCircle className="w-5 h-5 text-orange-500" />
              )}
              <span className="font-medium">Upload Complete</span>
            </div>
            <div className="space-y-2 text-sm">
              <p className="flex items-center gap-2">
                <span className="text-muted-foreground">Created:</span>
                <span className="font-medium text-green-600">{bulkResult.created_count} tags</span>
              </p>
              {bulkResult.error_count > 0 && (
                <p className="flex items-center gap-2">
                  <span className="text-muted-foreground">Errors:</span>
                  <span className="font-medium text-destructive">{bulkResult.error_count}</span>
                </p>
              )}
              {bulkResult.errors?.length > 0 && (
                <div className="mt-2 p-2 bg-destructive/10 rounded-lg max-h-32 overflow-y-auto">
                  {bulkResult.errors.slice(0, 5).map((err, i) => (
                    <p key={i} className="text-xs text-destructive">
                      {err.nfc_code}: {err.error}
                    </p>
                  ))}
                  {bulkResult.errors.length > 5 && (
                    <p className="text-xs text-muted-foreground mt-1">
                      +{bulkResult.errors.length - 5} more errors
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Help Text */}
        <div className="mt-6 text-sm text-muted-foreground">
          <p className="font-medium mb-2">CSV Format:</p>
          <code className="block bg-secondary/50 p-3 rounded-lg text-xs">
            nfc_code,description,message<br/>
            birthday-2024,Birthday Gift,Happy Birthday!<br/>
            wedding-memory,Wedding Day,Best day ever<br/>
            ,Auto-generated,Leave nfc_code empty for UUID
          </code>
        </div>
      </div>
    </div>
  );
};

export default BulkUpload;
